// useNotificationToast — bridges EventBus 'notification:new' → Toast system.
//
// Listens for new notifications on the Event Bus. For non-persistent
// notifications, auto-shows a toast. For persistent ones, the toast is
// skipped (they only appear in NotificationCenter).
//
// Mount once at App level (alongside ToastProvider + EventBusProvider).

import { useEffect } from 'react';
import { useEventBus } from './use-event-bus.js';
import { useToast, type ToastLevel } from '../../components/toast.js';

/** Map NotificationLevel to ToastLevel. */
function toToastLevel(level: string): ToastLevel {
  switch (level) {
    case 'success': return 'success';
    case 'warning': return 'warn';
    case 'error': return 'error';
    case 'info':
    default: return 'info';
  }
}

export function useNotificationToast(): void {
  const eventBus = useEventBus();
  const toast = useToast();

  useEffect(() => {
    const unsub = eventBus.on('notification:new', (payload) => {
      // Persistent notifications only go to NotificationCenter, not toast
      if (payload.persistent) return;

      const level = toToastLevel(payload.level);
      const message = payload.message
        ? `${payload.title} · ${payload.message}`
        : payload.title;

      const action = payload.action
        ? {
            label: payload.action.label,
            onClick: () => {
              payload.action?.onClick?.();
            },
          }
        : undefined;

      toast.show(level, message, {
        sticky: level === 'error',
        action,
        ...(payload.duration ? {} : {}), // uses ToastProvider default
      });
    });

    return unsub;
  }, [eventBus, toast]);
}
